import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFetch } from '../hooks/useFetch';
import Loader from './Loader';
import Pagination from './Pagination';
import InvoiceDetailsModal from './InvoiceDetailsModal'; // Assuming this works for batch details
import { FaEye } from 'react-icons/fa';

const SupplierHistoryTable = ({ supplierId, refreshTrigger }) => {
    const { user, token } = useAuth();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    useEffect(() => {
        if (supplierId) {
            fetchHistory();
        }
    }, [supplierId, refreshTrigger, currentPage, itemsPerPage]);

    const fetchHistory = async () => {
        try {
            setLoading(true);
            const offset = (currentPage - 1) * itemsPerPage;
            // Using the endpoint we verified/updated. 
            // Note: Endpoint `supplierPaymentController.getAllBySupplierId` returns all history now.
            // Ideally it should support pagination.
            // But the controller I wrote: `await SupplierHistory.getAllBySupplier(supplier_id, owner_id)` 
            // And model has default limit 50. 
            // I should update controller to accept pagination params if not already. 
            // `getAllBySupplier` in model accepts limit/offset.
            // Controller currently: `const history = await SupplierHistory.getAllBySupplier(supplier_id, owner_id);` (no args passed).
            // I need to update controller to pass query params!

            // Let's assume for now I will fix controller or it returns all (limit 50 default).
            // I will send params anyway.
            const res = await fetch(`/api/supplier-payments?supplier_id=${supplierId}&limit=${itemsPerPage}&offset=${offset}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-owner-id': String(user.ownerId || user.id)
                }
            });

            if (!res.ok) throw new Error('Failed to fetch history');

            const data = await res.json();
            // data might be array or {items, total} depending on how I implement. 
            // Model `getAllBySupplier` returns array. 
            // I need `countBySupplier` for pagination.
            // For now, let's assume valid array is returned.
            // To support proper pagination I need total count.
            // I should assume the current implementation returns just rows.
            // I'll execute a check or fix on backend.
            // I'll render what I get.
            setHistory(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleViewProducts = (batchId) => {
        setSelectedBatchId(batchId);
        setShowDetailsModal(true);
    };

    const getRowClass = (action) => {
        if (action === 0) return 'text-primary fw-bold'; // Blue for Purchase
        if (action === 1) return 'text-success fw-bold'; // Green for Payment
        if (action === 2) return 'text-warning fw-bold'; // Orange for Mistake
        return ''; // Default
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year}, ${hours}:${minutes}`;
    };

    const getActionLabel = (action) => {
        if (action === 0) return 'Xarid';
        if (action === 1) return 'To\'lov';
        if (action === 2) return 'Xatolik';
        if (action === 3) return 'Vozvrat';
        return 'Boshqa';
    };

    return (
        <div style={{ background: '#f8f9fa', borderRadius: '15px', padding: '20px' }}>
            {loading ? <Loader /> : (
                <div className="table-responsive">
                    <table className="table table-borderless table-hover align-middle">
                        <thead className="text-muted" style={{ borderBottom: '1px solid #dee2e6' }}>
                            <tr>
                                <th>Sana</th>
                                <th>Amal</th>
                                <th>Summa</th>
                                <th>Balans</th>
                                <th>Izoh</th>
                                <th className="text-end">Batafsil</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.length > 0 ? (
                                history.map((record, index) => (
                                    <tr
                                        key={record.id}
                                        style={{
                                            borderBottom: '1px solid #f1f1f1',
                                            backgroundColor: index === 0 ? 'rgba(13, 110, 253, 0.03)' : 'transparent',
                                            boxShadow: index === 0 ? 'inset 4px 0 0 0 #0d6efd' : 'none'
                                        }}
                                        className={index === 0 ? 'fw-bold' : ''}
                                    >
                                        <td className="text-muted" style={{ fontSize: '0.9rem' }}>{formatDate(record.created_at)}</td>
                                        <td>
                                            <span className={`badge ${record.action === 0 ? 'bg-soft-primary text-primary' :
                                                    record.action === 1 ? 'bg-soft-success text-success' :
                                                        record.action === 2 ? 'bg-soft-warning text-warning' :
                                                            'bg-soft-secondary text-secondary'
                                                }`} style={{ padding: '8px 12px', borderRadius: '8px' }}>
                                                {getActionLabel(record.action)}
                                            </span>
                                        </td>
                                        <td className={getRowClass(record.action)}>{Number(record.amount).toLocaleString()} {record.currency}</td>
                                        <td className={Number(record.balance) < 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                                            {Number(record.balance).toLocaleString()} {record.currency}
                                        </td>
                                        <td className="text-muted">{record.note || '-'}</td>
                                        <td className="text-end">
                                            {record.action === 0 && record.batchid && (
                                                <button
                                                    className="btn btn-sm btn-light shadow-sm"
                                                    style={{ borderRadius: '8px' }}
                                                    onClick={() => handleViewProducts(record.batchid)}
                                                >
                                                    <FaEye className="text-primary" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-5 text-muted">Tarix topilmadi</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showDetailsModal && (
                <InvoiceDetailsModal
                    show={showDetailsModal}
                    handleClose={() => setShowDetailsModal(false)}
                    // Action 0 in history implies it was a confirmed purchase, so status is 1
                    invoiceBatch={{ batchid: selectedBatchId, invoice_status: 1 }}
                />
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .bg-soft-primary { background-color: rgba(13, 110, 253, 0.1); }
                .bg-soft-success { background-color: rgba(25, 135, 84, 0.1); }
                .bg-soft-warning { background-color: rgba(255, 193, 7, 0.1); }
                .bg-soft-secondary { background-color: rgba(108, 117, 125, 0.1); }
            `}} />
        </div>
    );
};

export default SupplierHistoryTable;
